// SkyService Inside → SkyHub SSO relay.
//
// SkyHub (*.apps.platform365.online) logs users in with zero clicks by asking
// Inside for the current tenant context (companyId + token). Because SkyHub and
// Inside (inside.skyservice.online) are different origins, the browser forbids
// SkyHub from reading Inside's cookie/storage directly — so Inside hands the
// context over via postMessage.
//
// SkyHub opens Inside in one of two ways, passing its own origin as `relayTo`:
//   1. Silent  — hidden <iframe src=".../?relayTo=<origin>&silent=1"> → window.parent
//   2. Popup   — window.open(".../?relayTo=<origin>")                 → window.opener
//
// This module runs INSIDE Inside, reads Inside's own companyId+token, verifies
// the requester is allow-listed, and posts them back to the exact `relayTo`
// origin (never '*'). SkyHub validates event.origin before using the data.

import { storeS } from "@/store.js";
import Cookies from "js-cookie";

// Registry of SkyService products allowed to receive the tenant context via
// this relay. Inside is the identity + permission provider for the ecosystem;
// every consumer product (SkyHub, metric dashboards, …) uses the shared
// `@skyservice/inside-sso` client and must be listed here by its web origin.
// To onboard a new product: add its production origin below.
const ALLOWED_ORIGINS = [
  "https://1064ffdb-c181-4e3b-b4ad-0194d2253d10.apps.platform365.online", // SkyHub (prod)
  "https://d615bce5-b334-48ff-a4a8-b13a05ae70ac.apps.platform365.online", // HR Module (platform365, current)
  "https://7f1593d7-5f75-d591-d19d-37f33d3d77b5.app.platform365.online", // HR Module (platform365, old deploy)
  "https://hrmodule.skyservice.online", // HR Module (custom domain)
  // "https://your-metrics-product.example.com", // ← next product
];
if (process.env.NODE_ENV !== "production") {
  ALLOWED_ORIGINS.push("http://localhost:5173"); // local dev (any consumer)
}

// Where to post: popup → opener, iframe → parent. Normal top tab → nobody.
function destination() {
  if (window.opener && window.opener !== window) return window.opener;
  if (window.parent && window.parent !== window) return window.parent;
  return null;
}

// Current tenant context — the same values Inside sends to its own API.
// Prefer the reactive store; fall back to cookies and to the packed
// `savedTokenAndCompany` string ("<token>&companyId=<companyId>").
function getCompanyId() {
  if (storeS.companyId) return storeS.companyId;
  const cookie = Cookies.get("companyId");
  if (cookie) return cookie;
  const match = (storeS.savedTokenAndCompany || "").match(/companyId=([^&]+)/);
  return match ? match[1] : "";
}

function getToken() {
  if (storeS.token) return storeS.token;
  const cookie = Cookies.get("tokenInside"); // note: the cookie is "tokenInside"
  if (cookie) return cookie;
  const saved = storeS.savedTokenAndCompany || "";
  return saved ? saved.split("&")[0] : "";
}

export function initSkyhubRelay() {
  const dest = destination();
  if (!dest) return; // opened as a normal tab — nobody to relay to

  const relayTo =
    new URLSearchParams(window.location.search).get("relayTo") || "";
  if (ALLOWED_ORIGINS.indexOf(relayTo) === -1) return; // untrusted requester

  // Inside resolves its session asynchronously (initToken → getStart) — poll
  // briefly until the token + companyId are available.
  let tries = 0;
  const iv = setInterval(function () {
    tries++;
    const companyId = getCompanyId();
    const token = getToken();

    if (companyId && token) {
      clearInterval(iv);
      dest.postMessage(
        {
          type: "skyservice-auth",
          companyId: String(companyId),
          token: String(token),
        },
        relayTo // exact origin — never '*'
      );
      // A popup can close itself once delivered; a hidden iframe just stays.
      if (window.opener && window.opener !== window) {
        setTimeout(function () {
          try {
            window.close();
          } catch (e) {
            /* ignore */
          }
        }, 100);
      }
    } else if (tries > 50) {
      clearInterval(iv); // ~10s with no session → user isn't logged in; give up.
    }
  }, 200);
}
